import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Contrato } from '../types';
import paymentsService from '../services/payments.service';
import contractsService from '../services/contracts.service';

interface PagoRealFormData {
  contratoId: number;
  fechaPago: string;
  monto: number;
  montoBruto: number;
  montoNeto: number;
  detalleRetenciones: string;
  metodoPago: string;
  referencia: string;
  observaciones: string;
}

const PagoRealForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [formData, setFormData] = useState<PagoRealFormData>({
    contratoId: 0,
    fechaPago: new Date().toISOString().split('T')[0],
    monto: 0,
    montoBruto: 0,
    montoNeto: 0,
    detalleRetenciones: '',
    metodoPago: 'transferencia',
    referencia: '',
    observaciones: '',
  });

  // Estados para mostrar valores formateados mientras se edita
  const [displayValues, setDisplayValues] = useState({
    monto: '',
    montoBruto: '',
    montoNeto: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contratosData = await contractsService.getContratos(1, 1000);
        setContratos(contratosData.contratos || []);

        if (isEditing && id) {
          const pago = await paymentsService.getPagoRealById(parseInt(id));
          setFormData({
            contratoId: pago.contratoId,
            fechaPago: pago.fechaPago.split('T')[0],
            monto: pago.monto,
            montoBruto: pago.montoBruto || 0,
            montoNeto: pago.montoNeto || 0,
            detalleRetenciones: pago.detalleRetenciones || '',
            metodoPago: pago.metodoPago,
            referencia: pago.referencia || '',
            observaciones: pago.observaciones || '',
          });
          // Inicializar valores de visualización con formato
          setDisplayValues({
            monto: pago.monto > 0 ? pago.monto.toLocaleString('es-CO') : '',
            montoBruto: pago.montoBruto && pago.montoBruto > 0 ? pago.montoBruto.toLocaleString('es-CO') : '',
            montoNeto: pago.montoNeto && pago.montoNeto > 0 ? pago.montoNeto.toLocaleString('es-CO') : ''
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        alert('Error al cargar los datos');
      }
    };

    fetchData();
  }, [id, isEditing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'contratoId' ? parseInt(value) : value
    }));
  };

  // Manejar cambios en campos monetarios (mientras escribe)
  const handleMoneyInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Permitir solo números, punto y coma
    const cleanValue = value.replace(/[^\d.,]/g, '');
    
    // Actualizar el valor de visualización (lo que ve el usuario)
    setDisplayValues(prev => ({
      ...prev,
      [name]: cleanValue
    }));
  };

  // Cuando el campo pierde el foco, convertir a número y formatear
  const handleMoneyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Remover separadores y convertir a número
    const cleanValue = value.replace(/\./g, '').replace(/,/g, '.');
    const numericValue = parseFloat(cleanValue) || 0;
    
    // Actualizar el valor numérico en formData
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));

    // Formatear el valor de visualización
    setDisplayValues(prev => ({
      ...prev,
      [name]: numericValue > 0 ? numericValue.toLocaleString('es-CO') : ''
    }));
  };

  // Calcular retención automáticamente
  const retencionTotal = formData.montoBruto > 0 && formData.montoNeto > 0 
    ? formData.montoBruto - formData.montoNeto 
    : 0;
  const retencionPct = formData.montoBruto > 0 && retencionTotal > 0
    ? (retencionTotal / formData.montoBruto) * 100
    : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        contratoId: formData.contratoId,
        fechaPago: new Date(formData.fechaPago).toISOString(),
        monto: formData.monto,
        montoBruto: formData.montoBruto > 0 ? formData.montoBruto : undefined,
        montoNeto: formData.montoNeto > 0 ? formData.montoNeto : undefined,
        detalleRetenciones: formData.detalleRetenciones || undefined,
        metodoPago: formData.metodoPago,
        referencia: formData.referencia,
        observaciones: formData.observaciones,
      };

      if (isEditing && id) {
        await paymentsService.updatePagoReal(parseInt(id), submitData);
      } else {
        await paymentsService.createPagoReal(submitData);
      }
      navigate('/pagos-reales');
    } catch (error) {
      console.error('Error saving pago:', error);
      alert('Error al guardar el pago');
    } finally {
      setLoading(false);
    }
  };

  const selectedContrato = contratos.find(c => c.id === formData.contratoId);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/pagos-reales')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a pagos reales
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Pago Real' : 'Registrar Nuevo Pago Real'}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          {isEditing ? 'Actualiza la información del pago real' : 'Registra un pago que se realizó de forma independiente'}
        </p>
      </div>

      {/* Form */}
      <div className="max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Información del Pago */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Información del Pago Real
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="contratoId" className="block text-sm font-medium text-gray-700 mb-2">
                  Contrato *
                </label>
                <select
                  id="contratoId"
                  name="contratoId"
                  required
                  value={formData.contratoId}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={0}>Selecciona un contrato</option>
                  {contratos.map(contrato => (
                    <option key={contrato.id} value={contrato.id}>
                      {contrato.numeroContrato} - {contrato.cliente?.nombre}
                    </option>
                  ))}
                </select>
                {selectedContrato && (
                  <p className="mt-2 text-sm text-gray-600">
                    Valor del contrato: ${selectedContrato.valorTotal.toLocaleString('es-CO')}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="fechaPago" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha del Pago *
                </label>
                <input
                  type="date"
                  id="fechaPago"
                  name="fechaPago"
                  required
                  value={formData.fechaPago}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="monto" className="block text-sm font-medium text-gray-700 mb-2">
                  Monto del Pago (Legacy) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    id="monto"
                    name="monto"
                    required
                    value={displayValues.monto}
                    onChange={handleMoneyInputChange}
                    onBlur={handleMoneyBlur}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Campo legacy, usar Monto Bruto y Neto para retenciones</p>
              </div>

              <div>
                <label htmlFor="montoBruto" className="block text-sm font-medium text-gray-700 mb-2">
                  Monto Bruto (Factura)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    id="montoBruto"
                    name="montoBruto"
                    value={displayValues.montoBruto}
                    onChange={handleMoneyInputChange}
                    onBlur={handleMoneyBlur}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Valor total de la factura antes de retenciones</p>
              </div>

              <div>
                <label htmlFor="montoNeto" className="block text-sm font-medium text-gray-700 mb-2">
                  Monto Neto Recibido
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    id="montoNeto"
                    name="montoNeto"
                    value={displayValues.montoNeto}
                    onChange={handleMoneyInputChange}
                    onBlur={handleMoneyBlur}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="0"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Valor efectivamente recibido después de retenciones</p>
              </div>

              <div>
                <label htmlFor="metodoPago" className="block text-sm font-medium text-gray-700 mb-2">
                  Método de Pago *
                </label>
                <select
                  id="metodoPago"
                  name="metodoPago"
                  required
                  value={formData.metodoPago}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="transferencia">Transferencia Bancaria</option>
                  <option value="cheque">Cheque</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta">Tarjeta de Crédito</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="referencia" className="block text-sm font-medium text-gray-700 mb-2">
                  Referencia/Comprobante
                </label>
                <input
                  type="text"
                  id="referencia"
                  name="referencia"
                  value={formData.referencia}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Número de referencia o comprobante"
                />
              </div>
            </div>

            {/* Banner de cálculo de retención */}
            {retencionTotal > 0 && (
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-yellow-800">Retención Calculada</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        <strong>Retención Total:</strong> ${retencionTotal.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {' '}({retencionPct.toFixed(2)}%)
                      </p>
                      <p className="mt-1 text-xs">
                        Monto Bruto: ${formData.montoBruto.toLocaleString('es-CO')} - Monto Neto: ${formData.montoNeto.toLocaleString('es-CO')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <label htmlFor="detalleRetenciones" className="block text-sm font-medium text-gray-700 mb-2">
                Detalle de Retenciones
              </label>
              <textarea
                id="detalleRetenciones"
                name="detalleRetenciones"
                rows={3}
                value={formData.detalleRetenciones}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ej: IVA: $159.664, Estampilla Prohospital: $20.000, Retención Fuente: $60.000"
              />
              <p className="mt-1 text-xs text-gray-500">Detalle de los descuentos aplicados (opcional)</p>
            </div>

            <div className="mt-6">
              <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                id="observaciones"
                name="observaciones"
                rows={4}
                value={formData.observaciones}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Observaciones del pago (ej. Pago parcial, abono a cuenta, etc.)"
              />
            </div>
          </div>

          {/* Summary Card */}
          {selectedContrato && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-blue-900 mb-4">Resumen del Contrato</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <dt className="text-sm font-medium text-blue-700">Cliente</dt>
                  <dd className="mt-1 text-sm text-blue-900">{selectedContrato.cliente?.nombre}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-blue-700">Valor Total</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">
                    ${selectedContrato.valorTotal.toLocaleString('es-CO')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-blue-700">Estado</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedContrato.estado === 'vigente'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {selectedContrato.estado}
                    </span>
                  </dd>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/pagos-reales')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || formData.contratoId === 0}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Registrar Pago Real')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PagoRealForm;